import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { OrnamentDivider, Fleuron } from "@/components/Ornament";
import { PageHeaderBanner } from "@/components/PageHeaderBanner";
import { ScrollReveal } from "@/components/ScrollReveal";
import { ArrowRight, Phone } from "lucide-react";
import { WINE_CATEGORIES, localiseRegion } from "@/data/wines";

const WineList = () => {
  const { t, lang } = useLanguage();

  return (
    <div data-testid="wine-page" className="pb-24 bg-sky-roze paper-grain relative overflow-hidden">
      <PageHeaderBanner
        testid="wine-header-banner"
        imgSrc="/illustrations/menu_table.png"
        eyebrow={t("menu.wine.eyebrow")}
        title={t("menu.wine.title")}
        lead={t("menu.wine.lead")}
      />

      <section className="container-roze relative pt-16 md:pt-24">
        <OrnamentDivider className="mb-16" />

        {/* Categories */}
        <div className="space-y-20 md:space-y-28">
          {WINE_CATEGORIES.map((cat, sIdx) => {
            const sec = t(`menu.wine.sections.${cat.key}`) || {};
            return (
              <ScrollReveal
                key={cat.key}
                data-testid={`wine-category-${cat.key}`}
                className="relative"
              >
                {/* Category header */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 mb-10 md:mb-12 items-end">
                  <div className="md:col-span-9">
                    <div className="overline text-[#1F5C8A] mb-3 flex items-center gap-3">
                      <span className="font-serif italic normal-case tracking-normal text-base text-[#1F5C8A]/80">
                        {String(sIdx + 1).padStart(2, "0")}.
                      </span>
                      {sec.subtitle}
                    </div>
                    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light italic leading-none tracking-tight">
                      {sec.title}
                    </h2>
                  </div>
                  <div className="md:col-span-3 flex items-end justify-end">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-foreground/55 text-right leading-relaxed">
                      {t("menu.wine.labels.glass_bottle")}
                      <br />
                      <span className="text-foreground/40">€</span>
                    </span>
                  </div>
                </div>

                {/* Wines list */}
                <ul className="border-t border-[#1F5C8A]/20">
                  {cat.items.map((w, i) => (
                    <li
                      key={i}
                      data-testid={`wine-item-${cat.key}-${i}`}
                      className="grid grid-cols-[1fr_auto] md:grid-cols-12 gap-3 md:gap-8 py-5 md:py-6 border-b border-[#1F5C8A]/15 hover:bg-white/40 transition-colors"
                    >
                      {/* Name + meta */}
                      <div className="md:col-span-9 min-w-0">
                        <div className="font-serif text-lg md:text-xl text-foreground leading-tight">
                          {w.name}
                        </div>
                        <div className="mt-1.5 text-[12px] md:text-sm text-foreground/65 leading-relaxed font-light italic">
                          {[
                            localiseRegion(w.region, lang),
                            w.grape,
                            w.year,
                            w.volume,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </div>

                      {/* Price */}
                      <div className="md:col-span-3 flex md:justify-end items-start md:items-center">
                        <span className="font-serif text-lg md:text-xl text-[#1F5C8A] tabular-nums whitespace-nowrap">
                          {w.price}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </ScrollReveal>
            );
          })}
        </div>

        {/* Note */}
        <ScrollReveal className="mt-20 md:mt-24 max-w-3xl mx-auto text-center">
          <OrnamentDivider className="mb-10" />
          <p className="text-sm md:text-base text-foreground/60 italic font-light leading-relaxed">
            {t("menu.wine.note")}
          </p>
        </ScrollReveal>

        {/* CTA */}
        <ScrollReveal className="mt-16 md:mt-20 bg-white/70 border border-gold/40 p-10 md:p-14 paper-grain relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <div className="overline mb-4 flex items-center gap-3">
                <Fleuron className="h-3 w-3 text-gold" />
                {t("home.cta_band_overline")}
              </div>
              <h2 className="font-serif text-3xl md:text-5xl font-light leading-tight tracking-tight italic">
                Salute!
              </h2>
              <p className="mt-4 text-base md:text-lg text-foreground/75 leading-relaxed max-w-xl font-light">
                {t("home.cta_band_body")}
              </p>
            </div>
            <div className="md:col-span-4 flex flex-col gap-3 md:items-end">
              <Link
                to="/reserve"
                data-testid="wine-cta-reserve"
                className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-xs uppercase tracking-[0.22em]"
              >
                {t("nav.reserve")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
              </Link>
              <a
                href="tel:+37126111722"
                data-testid="wine-cta-phone"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-gold/60 hover:bg-white/60 transition-colors text-xs uppercase tracking-[0.22em] text-foreground/85"
              >
                <Phone className="h-3.5 w-3.5" />
                +371 26 111 722
              </a>
            </div>
          </div>
        </ScrollReveal>
      </section>
    </div>
  );
};

export default WineList;
